import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import './Badges.css';

const Badges = () => {
  const { t } = useTranslation();
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const filterOptions = [
    { label: t('badges.allBadges'), value: 'all' },
    { label: t('badges.filterEarned'), value: 'earned' },
    { label: t('badges.filterLocked'), value: 'locked' },
  ];

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const [allRes, myRes, pointsRes] = await Promise.all([
        api.get('/badges'),
        api.get('/badges/my-badges'),
        api.get('/badges/points'),
      ]);
      setAllBadges(allRes.data);
      setMyBadges(myRes.data);
      setTotalPoints(pointsRes.data.total_points);
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const isEarned = (badgeId) => myBadges.some((b) => b.id === badgeId);

  const getEarnedDate = (badgeId) => {
    const badge = myBadges.find((b) => b.id === badgeId);
    return badge ? new Date(badge.earned_at).toLocaleDateString() : null;
  };

  const earnedCount = myBadges.length;
  const remaining = Math.max(allBadges.length - earnedCount, 0);
  const completion = allBadges.length
    ? Math.round((earnedCount / allBadges.length) * 100)
    : 0;

  const filteredBadges = allBadges.filter((badge) => {
    if (filter === 'earned') return isEarned(badge.id);
    if (filter === 'locked') return !isEarned(badge.id);
    return true;
  });

  if (loading) return <div className="loading">{t('badges.loading')}</div>;

  return (
    <div className="page badges-page">
      <div className="container">
        <section className="badges-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('badges.achievementsEyebrow')}</p>
            <h1>{t('badges.title')}</h1>
            <p>{t('badges.subtitle')}</p>
            <div className="hero-stats">
              <div>
                <span className="label">{t('badges.totalPoints')}</span>
                <strong>{totalPoints}</strong>
              </div>
              <div>
                <span className="label">{t('badges.earned')}</span>
                <strong>{earnedCount}</strong>
                <span className="meta">{t('badges.of')} {allBadges.length}</span>
              </div>
              <div>
                <span className="label">{t('badges.remaining')}</span>
                <strong>{remaining}</strong>
              </div>
            </div>
          </div>
          <div className="hero-card">
            <div className="hero-progress-text">
              <span className="label">{t('badges.collectionProgress')}</span>
              <span className="value">{completion}%</span>
            </div>
            <p className="hero-caption">{t('badges.unlockCaption')}</p>
            <div className="hero-highlight">
              {myBadges.slice(0, 3).map((badge) => (
                <div key={badge.id} className="highlight-item">
                  <span>{getBadgeEmoji(badge.name)}</span>
                  <div>
                    <p>{badge.name}</p>
                    <small>
                      {t('badges.earnedOn')} {new Date(badge.earned_at).toLocaleDateString()}
                    </small>
                  </div>
                </div>
              ))}
              {myBadges.length === 0 && (
                <p className="empty-highlight">{t('badges.earnFirst')}</p>
              )}
            </div>
          </div>
        </section>

        <div className="badge-toolbar">
          <div className="filter-pills">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`pill ${filter === option.value ? 'active' : ''}`}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="toolbar-meta">
            {filteredBadges.length} badge{filteredBadges.length === 1 ? '' : 's'}
            {filter !== 'all' ? ` • ${filter}` : ''}
          </p>
        </div>

        <div className="badges-grid">
          {filteredBadges.map((badge) => {
            const earned = isEarned(badge.id);
            const earnedDate = getEarnedDate(badge.id);

            return (
              <article
                key={badge.id}
                className={`badge-card ${earned ? 'earned' : 'locked'}`}
              >
                <div className="badge-status-row">
                  <span className={`status ${earned ? 'unlocked' : 'locked'}`}>
                    {earned ? t('badges.unlocked') : t('badges.locked')}
                  </span>
                  <span className="points-tag">+{badge.points} pts</span>
                </div>
                <div className="badge-icon">
                  {earned ? getBadgeEmoji(badge.name) : '🔒'}
                </div>
                <h3>{badge.name}</h3>
                <p className="badge-description">{badge.description}</p>
                {earned && earnedDate ? (
                  <p className="earned-date">{t('badges.earnedOn')} {earnedDate}</p>
                ) : (
                  <p className="badge-status">{t('badges.keepLearning')}</p>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const getBadgeEmoji = (name) => {
  const emojiMap = {
    'First Steps': '👣',
    'Quick Learner': '🚀',
    'Course Champion': '🏆',
    'Quiz Master': '🎯',
    'Dedicated Student': '🔥',
    'Scholar': '🎓',
  };
  return emojiMap[name] || '⭐';
};

export default Badges;
